import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  createPemserterPriceListEur,
  deletePemserterPriceListEur,
  getPemserterPriceListEur,
  listPemserterPriceListEur,
  updatePemserterPriceListEur,
  type CreatePemserterPriceListEurBody,
  type ListPemserterPriceListEurParams,
  type PaginatedPemserterPriceListEur,
  type PemserterPriceListEur,
  type UpdatePemserterPriceListEurBody,
} from "../api/pemserterPriceListEur";

const KEY = "pemserter-price-list-eur";

export function usePemserterPriceListEur(params: ListPemserterPriceListEurParams) {
  return useQuery<PaginatedPemserterPriceListEur>({
    queryKey: [KEY, "list", params],
    queryFn: () => listPemserterPriceListEur(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function usePemserterPriceListEurDetail(rowId: number | null) {
  return useQuery<PemserterPriceListEur>({
    queryKey: [KEY, "detail", rowId],
    queryFn: () => getPemserterPriceListEur(rowId as number),
    enabled: rowId !== null,
    staleTime: 30_000,
  });
}

export function useCreatePemserterPriceListEur() {
  const queryClient = useQueryClient();
  return useMutation<PemserterPriceListEur, Error, CreatePemserterPriceListEurBody>({
    mutationFn: (body) => createPemserterPriceListEur(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

export function useUpdatePemserterPriceListEur() {
  const queryClient = useQueryClient();
  return useMutation<
    PemserterPriceListEur,
    Error,
    { rowId: number; body: UpdatePemserterPriceListEurBody }
  >({
    mutationFn: ({ rowId, body }) => updatePemserterPriceListEur(rowId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

/** Soft-delete via DELETE /v1/pemserter-price-list-eur/:id. */
export function useDeletePemserterPriceListEur() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (rowId) => deletePemserterPriceListEur(rowId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}
